import { useMutation } from 'hooks/useMutation'
import { useQuery } from 'hooks/useQuery'
import { useQueryClient } from 'hooks/useQueryClient'

import {
  dislikePost as dislikePostQuery,
  isPostLiked,
  likePost as likePostQuery,
} from 'utils/queries/posts'

import { Data } from 'types/query'

type Params = {
  userId?: string
  postId: string
}

export const queryHasPost = (data: any, postId: string) => {
  return data?.pages?.some((page: any) => {
    const index = page.findIndex((post: any) => post.id === postId)
    return index > -1
  })
}

const updateLikesInPost = (data: any, postId: string, likesToAdd: number) => {
  const newPages = data.pages.map((page: any) => {
    const postIndex = page.findIndex((post: any) => post.id === postId)
    if (postIndex > -1) {
      const post = page[postIndex]
      post.likedCount += likesToAdd
      if (post.likedCount < 0) {
        post.likedCount = 0
      }
      // eslint-disable-next-line no-param-reassign
      page[postIndex] = post
      return page
    }
    return page
  })
  return { ...data, pages: newPages }
}

export const useLike = ({ postId, userId }: Params) => {
  const queryClient = useQueryClient()

  const likeQuery = useQuery<boolean>(
    ['like', userId, postId],
    () => isPostLiked({ userId, postId }),
    {
      enabled: !!userId,
    },
  )

  /**
   * Adds a like for the current user to the Likes subcollection of a Post.
   * Also optimisitically updates the query cache so the UI updates immediately
   * while triggers run in the background to update `likedCount` on the Post itself.
   */
  const likePost = useMutation<unknown, unknown>(
    (data: Data) => {
      return likePostQuery(data)
    },
    {
      retry: 3,
      onMutate: async () => {
        const isCurrentlyLiked = await queryClient.getQueryData(['like', userId, postId])
        queryClient.setQueryData(['like', userId, postId], true)
        queryClient.setQueriesData('posts', (data: Data) => {
          if (data && queryHasPost(data, postId)) {
            return updateLikesInPost(data, postId, 1)
          }
          return data
        })
        queryClient.setQueryData(['post', postId], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.likedCount += 1
          }
          return data
        })

        const post = await queryClient.getQueryData(['posts', postId])
        return {
          isCurrentlyLiked,
          post,
        }
      },
      onError: (_err, _variables, context: any) => {
        queryClient.setQueryData(['like', userId, postId], context.isCurrentlyLiked)

        queryClient.setQueriesData('posts', (data: Data) => {
          if (data && queryHasPost(data, postId)) {
            return updateLikesInPost(data, postId, context?.post?.likedCount ?? 1)
          }
          return data
        })
      },
    },
  )

  /**
   * Removes a like for the current user to the Likes subcollection of a Post.
   * Also optimisitically updates the query cache so the UI updates immediately
   * while triggers run in the background to update `likedCount` on the Post itself.
   */
  const dislikePost = useMutation(
    (data: Data) => {
      return dislikePostQuery(data)
    },
    {
      onSuccess: async () => {
        queryClient.setQueryData(['like', userId, postId], false)
        queryClient.setQueriesData('posts', (data: Data) => {
          if (data && queryHasPost(data, postId)) {
            return updateLikesInPost(data, postId, -1)
          }
          return data
        })

        queryClient.setQueryData(['post', postId], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.likedCount -= 1
          }
          return data
        })
      },
    },
  )

  return {
    ...likeQuery,
    likePost,
    dislikePost,
  }
}
